import { useEffect } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import { ArrowLeft, ArrowUpRight } from "lucide-react";
import { gsap } from "@/lib/gsap";
import { useT } from "@/i18n";
import { SERVICES_LIST } from "@/data/services";
import Navbar from "@/components/Navbar";
import Footer from "@/components/Footer";

export default function ServiceDetail() {
  const { slug } = useParams();
  const navigate = useNavigate();
  const { t, lang } = useT();
  const service = SERVICES_LIST.find((s) => s.slug === slug);

  useEffect(() => {
    window.scrollTo(0, 0);
    if (service) {
      document.title = `${service.name} — Makario IT Consulting`;
      const desc = service.tagline[lang] || service.tagline.en;
      let meta = document.querySelector('meta[name="description"]');
      if (!meta) {
        meta = document.createElement("meta");
        meta.name = "description";
        document.head.appendChild(meta);
      }
      meta.content = `${service.name} di Indonesia — ${desc}`;
    }
  }, [service, lang]);

  useEffect(() => {
    if (!service) return;
    const ctx = gsap.context(() => {
      gsap.from("[data-anim='sd-title']", { opacity: 0, y: 50, duration: 1.05, ease: "power3.out", delay: 0.05 });
      gsap.from("[data-anim='sd-tag']", { opacity: 0, y: 30, duration: 0.9, delay: 0.25 });
      gsap.from("[data-anim='sd-mark']", { opacity: 0, scale: 0.94, duration: 1.4, ease: "power3.out" });
      gsap.from("[data-anim='sd-row']", {
        opacity: 0, y: 40, duration: 0.85, stagger: 0.12,
        scrollTrigger: { trigger: "[data-anim='sd-body']", start: "top 80%" },
      });
    });
    return () => ctx.revert();
  }, [service]);

  if (!service) {
    return (
      <main className="min-h-screen flex items-center justify-center text-[var(--ink)]">
        <div className="text-center">
          <p className="h-sub">{t("detail.notFound")}</p>
          <button onClick={() => navigate("/")} className="btn-pill mt-8 text-[var(--ink)] border-[var(--ink)]">
            <ArrowLeft size={14} /> {t("detail.back")}
          </button>
        </div>
      </main>
    );
  }

  const serviceIdx = SERVICES_LIST.findIndex((s) => s.slug === service.slug);
  // 2-letter code derived from initials
  const code = service.name
    .replace(/[^A-Za-z+ ]/g, "")
    .split(/\s+/)
    .filter(Boolean)
    .map((w) => w[0])
    .join("")
    .slice(0, 3)
    .toUpperCase();

  return (
    <main data-testid="service-detail" className="normal-scroll">
      <Navbar />

      {/* HERO — editorial */}
      <section className="relative min-h-screen flex flex-col px-6 sm:px-8 md:px-16 lg:px-24 pt-36 md:pt-32 pb-16 overflow-hidden grain" style={{ background: "var(--ink)" }}>
        <div
          className="absolute pointer-events-none rounded-full"
          style={{
            width: 950, height: 950, top: "-22%", right: "-15%",
            background: `radial-gradient(circle, ${service.accent} 0%, transparent 62%)`,
            opacity: 0.18, filter: "blur(40px)",
          }}
        />
        <div
          className="absolute inset-0 pointer-events-none opacity-[0.05]"
          style={{
            backgroundImage: "linear-gradient(90deg, transparent calc(100% - 1px), white calc(100% - 1px))",
            backgroundSize: "12.5% 100%",
          }}
        />

        {/* Top eyebrow row */}
        <div className="relative z-10 flex items-center justify-between mb-12 md:mb-16">
          <Link
            to="/#services"
            data-testid="sd-back"
            className="inline-flex items-center gap-2 text-white/65 hover:text-white text-[0.65rem] font-mono uppercase tracking-[0.28em]"
          >
            <ArrowLeft size={14} /> {t("detail.back")}
          </Link>
          <div className="flex items-center gap-6">
            <span className="font-mono text-[0.62rem] uppercase tracking-[0.3em] text-white/45">
              {String(serviceIdx + 1).padStart(2, "0")} / {String(SERVICES_LIST.length).padStart(2, "0")}
            </span>
            <span className="font-mono text-[0.62rem] uppercase tracking-[0.3em] text-white/45">
              {t("nav.services")}
            </span>
          </div>
        </div>

        {/* Main editorial grid */}
        <div className="relative z-10 flex-1 grid grid-cols-1 lg:grid-cols-12 gap-12 lg:gap-16 items-center">
          <div className="lg:col-span-7 text-white">
            <div className="flex items-center gap-3 mb-7">
              <span
                className="font-mono text-[0.6rem] uppercase tracking-[0.28em] px-2.5 py-1 rounded-sm"
                style={{ background: service.accent, color: service.accent === "#b8e637" ? "#0c0e14" : "#fff" }}
              >
                {code}
              </span>
              <span className="font-mono text-[0.65rem] uppercase tracking-[0.28em] text-white/55">
                {lang === "id" ? "Disiplin" : "Discipline"}
              </span>
            </div>
            <h1
              data-anim="sd-title"
              data-testid="sd-title"
              className="font-display text-white"
              style={{
                fontSize: "clamp(2.6rem, 6.4vw, 5.8rem)",
                lineHeight: 0.95,
                letterSpacing: "-0.035em",
                fontWeight: 700,
              }}
            >
              {service.name}
            </h1>
            <p
              data-anim="sd-tag"
              className="mt-10 max-w-[34rem] font-display italic"
              style={{
                fontSize: "clamp(1.05rem, 1.6vw, 1.4rem)",
                lineHeight: 1.4,
                color: service.accent,
                fontWeight: 500,
                fontFamily: "Cormorant Garamond, serif",
              }}
            >
              {service.tagline[lang]}
            </p>
            <p className="mt-8 max-w-[40rem] text-white/65 text-[15px] leading-relaxed">
              {service.intro[lang]}
            </p>
          </div>

          {/* Watermark code */}
          <div className="lg:col-span-5 hidden lg:flex items-center justify-center">
            <div
              data-anim="sd-mark"
              className="font-display select-none leading-none"
              style={{
                fontSize: "clamp(8rem, 22vw, 22rem)",
                fontWeight: 800,
                letterSpacing: "-0.06em",
                color: service.accent,
                opacity: 0.16,
                textShadow: `0 0 80px ${service.accent}40`,
              }}
            >
              {code}
            </div>
          </div>
        </div>

        {/* Bottom rule */}
        <div className="relative z-10 mt-12 pt-6 border-t border-white/10 flex items-center justify-between font-mono text-[0.6rem] uppercase tracking-[0.28em] text-white/35">
          <span>{lang === "id" ? "Disiplin Makario" : "A Makario discipline"}</span>
          <span>{code} · {String(serviceIdx + 1).padStart(2, "0")}/{String(SERVICES_LIST.length).padStart(2, "0")}</span>
        </div>
      </section>

      {/* BODY — spec sheet */}
      <section
        data-anim="sd-body"
        className="relative px-6 sm:px-8 md:px-16 lg:px-24 py-24 md:py-36"
        style={{ background: "var(--paper)" }}
      >
        <div className="max-w-[1240px] mx-auto space-y-24">
          {/* Deliverables */}
          <div data-anim="sd-row" className="grid grid-cols-1 lg:grid-cols-12 gap-10">
            <div className="lg:col-span-4">
              <div className="font-mono text-[0.6rem] uppercase tracking-[0.32em] text-black/40 mb-3">
                /01 · {lang === "id" ? "Yang Anda Dapatkan" : "What you get"}
              </div>
              <h2 className="font-display text-[var(--ink)]" style={{ fontSize: "clamp(1.6rem,2.6vw,2.2rem)", letterSpacing: "-0.02em", fontWeight: 600, lineHeight: 1.1 }}>
                {lang === "id" ? "Hasil nyata." : "Tangible outputs."}
              </h2>
            </div>
            <ul className="lg:col-span-8 grid grid-cols-1 sm:grid-cols-2 gap-x-10 gap-y-0 border-t border-black/10">
              {service.deliverables[lang].map((d, i) => (
                <li key={d} className="flex items-baseline gap-4 py-4 border-b border-black/10">
                  <span className="font-mono text-[0.6rem] tracking-[0.24em] text-black/40 flex-none">
                    /{String(i + 1).padStart(2, "0")}
                  </span>
                  <span className="text-[15px] leading-snug text-black/75">{d}</span>
                </li>
              ))}
            </ul>
          </div>

          {/* Process */}
          <div data-anim="sd-row" className="grid grid-cols-1 lg:grid-cols-12 gap-10">
            <div className="lg:col-span-4">
              <div className="font-mono text-[0.6rem] uppercase tracking-[0.32em] text-black/40 mb-3">
                /02 · {lang === "id" ? "Proses Kerja" : "Our process"}
              </div>
              <h2 className="font-display text-[var(--ink)]" style={{ fontSize: "clamp(1.6rem,2.6vw,2.2rem)", letterSpacing: "-0.02em", fontWeight: 600, lineHeight: 1.1 }}>
                {lang === "id" ? "Tahap demi tahap." : "Step by step."}
              </h2>
            </div>
            <ol className="lg:col-span-8 border-t border-black/10">
              {service.process[lang].map((step, i) => (
                <li key={step} className="flex items-baseline gap-6 py-5 border-b border-black/10">
                  <span
                    className="font-display flex-none"
                    style={{
                      fontSize: "clamp(1.4rem, 2.2vw, 1.9rem)",
                      letterSpacing: "-0.02em",
                      fontWeight: 700,
                      color: service.accent === "#b8e637" ? "#0c0e14" : service.accent,
                    }}
                  >
                    {String(i + 1).padStart(2, "0")}
                  </span>
                  <span className="text-[15px] text-black/75 leading-relaxed">{step}</span>
                </li>
              ))}
            </ol>
          </div>

          {/* CTA strip */}
          <div data-anim="sd-row" className="relative rounded-2xl overflow-hidden p-10 md:p-14" style={{ background: "var(--ink)" }}>
            <div
              className="absolute pointer-events-none rounded-full"
              style={{
                width: 480, height: 480, top: "-40%", right: "-10%",
                background: `radial-gradient(circle, ${service.accent} 0%, transparent 60%)`,
                opacity: 0.22, filter: "blur(30px)",
              }}
            />
            <div className="relative flex flex-col md:flex-row gap-8 md:items-end md:justify-between">
              <div className="text-white max-w-[34rem]">
                <div className="font-mono text-[0.6rem] uppercase tracking-[0.32em] text-white/45 mb-3">
                  /03 · {lang === "id" ? "Mulai" : "Start"}
                </div>
                <p className="font-display text-white" style={{ fontSize: "clamp(1.4rem,2.2vw,1.9rem)", letterSpacing: "-0.02em", fontWeight: 500, lineHeight: 1.2 }}>
                  {lang === "id"
                    ? `Diskusikan ${service.name.toLowerCase()} untuk konteks bisnis Anda.`
                    : `Discuss ${service.name.toLowerCase()} for your business context.`}
                </p>
              </div>
              <Link
                to="/#contact"
                className="inline-flex items-center gap-3 self-start md:self-auto font-mono text-[0.65rem] uppercase tracking-[0.28em] text-white border border-white/30 rounded-full px-5 py-3 hover:bg-white hover:text-black transition-all duration-300"
              >
                {t("nav.cta")} <ArrowUpRight size={14} />
              </Link>
            </div>
          </div>

          {/* Other disciplines */}
          <div data-anim="sd-row" className="pt-10">
            <div className="font-mono text-[0.6rem] uppercase tracking-[0.32em] text-black/40 mb-8">
              /04 · {lang === "id" ? "Disiplin Lainnya" : "Other disciplines"}
            </div>
            <ol className="border-t border-black/10">
              {SERVICES_LIST.filter((s) => s.slug !== service.slug).map((s, i) => (
                <li key={s.slug} className="border-b border-black/10">
                  <Link
                    to={`/services/${s.slug}`}
                    data-testid={`sd-related-${s.slug}`}
                    className="group flex items-center justify-between gap-6 py-5"
                  >
                    <div className="flex items-baseline gap-6 min-w-0">
                      <span
                        className="font-mono text-[0.6rem] uppercase tracking-[0.28em] flex-none"
                        style={{ color: s.accent === "#b8e637" ? "#7e9e23" : s.accent }}
                      >
                        /{String(i + 1).padStart(2, "0")}
                      </span>
                      <span
                        className="font-display text-[var(--ink)] truncate transition-all duration-300 group-hover:translate-x-1"
                        style={{ fontSize: "clamp(1.2rem,2.1vw,1.7rem)", letterSpacing: "-0.02em", fontWeight: 600 }}
                      >
                        {s.name}
                      </span>
                      <span className="hidden md:inline text-sm text-black/45 truncate font-display italic" style={{ fontFamily: "Cormorant Garamond, serif" }}>
                        — {s.tagline[lang]}
                      </span>
                    </div>
                    <ArrowUpRight size={18} className="text-black/35 group-hover:text-[var(--violet)] transition-colors flex-none" />
                  </Link>
                </li>
              ))}
            </ol>
          </div>
        </div>
      </section>

      <Footer />
    </main>
  );
}
